// 判断值是否为数字
export const isNumeric = (value: unknown): boolean => {
  if (typeof value === "number") return Number.isFinite(value);
  if (typeof value === "string")
    return !isNaN(parseFloat(value)) && isFinite(Number(value));
  return false;
};
// 将字符串转成数字
export const convertToNumber = <T>(input: T): T => {
  // 处理 null 和 undefined
  if (input === null || input === undefined) return input;

  // 处理字符串
  if (typeof input === "string") {
    const num = Number(input);
    return (isNaN(num) ? input : num) as T;
  }

  // 处理数组
  if (Array.isArray(input)) {
    return input.map((item) => convertToNumber(item)) as T;
  }

  // 处理对象
  if (typeof input === "object") {
    const result: any = {};
    for (const key in input) {
      result[key] = convertToNumber(input[key]);
    }
    return result as T;
  }

  // 其他类型直接返回
  return input;
};

// 合并数组
// nestedArrays : 嵌套数组，[[18,1],[18,3],[18,2]]
// singleArrays: [18,1],[18,3],[18,2]

// 数字排序
// merge([18, 1], [18, 2], [18, 3]);
// [1, 2, 3, 18]

// 字符串排序
// merge(["a", "b"], ["a", "c"], ["a", "r"]);
// ["a", "b", "c", "r"]

// 中文排序
// merge(["张三", "李四"], ["王五", "张三"]);
// ["李四", "王五", "张三"]（按拼音排序）
export const merge = <T>(
  sort?: "asc" | "desc" | "none",
  ...args: T[][] | [T[][]]
): T[] => {
  const arrays = Array.isArray(args[0][0])
    ? (args[0] as T[][])
    : (args as T[][]);
  const unique = [...new Set(arrays.flat())];
  if (sort !== "none") {
    return unique.sort((a, b) => {
      if (typeof a === "number" && typeof b === "number") {
        return sort === "asc" ? a - b : b - a; // 数字升序
      }
      return sort === "asc"
        ? String(a).localeCompare(String(b))
        : String(b).localeCompare(String(a)); // 字符串字母排序
    });
  } else return unique;
};




// 删除数组中指定值
// removeFromArray([1, 2, 3, 4], 3);           // [1, 2, 4]
export function arrayRemoveByValue<T>(arr: T[], value: T): T[] {
  return arr.filter((item) => item !== value);
}

// 删除对象数组中指定项，按 key 匹配
// const hours = [
//   { day: "周一", hours: "09:00-21:00" },
//   { day: "周二", hours: "09:00-21:00" },
//   { day: "周三", hours: "09:00-21:00" },
// ];
// removeByKey(hours, "day", "周二");
export function arrayRemoveByKey<T, K extends keyof T>(
  arr: T[],
  key: K,
  value: T[K],
): T[] {
  return arr.filter((item) => item[key] !== value);
}

// 按索引删除
// removeByIndex(["a", "b", "c"], 1); // ["a", "c"]
export function arrayRemoveByIndex<T>(arr: T[], index: number): T[] {
  return arr.filter((_, i) => i !== index);
}


// ========================== 使用示例 ==========================
// const result = mergeObjects(
//   { role: true },
//   { permission: true },
//   { user: true }
// );
// console.log(result); // { role: true, permission: true, user: true }
export const mergeObjects = (...objects: Record<string, any>[]): Record<string, any> => {
  return Object.assign({}, ...objects);
};

// ========================== 使用示例 ==========================
// const result = flattenIndexedObject({
//   "0": { role: true },
//   "1": { permission: true },
//   "2": { user: true },
// });
// console.log(result);
// { role: true, permission: true, user: true }
export const flattenIndexedObject = (
  items: Record<string, boolean>[] | Record<string, Record<string, boolean>>
): Record<string, boolean> => {
  // 如果是数组，直接处理
  if (Array.isArray(items)) {
    return items.reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {} as Record<string, boolean>
    );
  }

  // 如果是对象，用 Object.values 处理
  return Object.values(items).reduce(
    (acc, obj) => ({ ...acc, ...obj }),
    {} as Record<string, boolean>
  );
};
// ========================== 使用示例 ==========================
// const result = flattenNestedPermissions([
//   [{ role: true }, { permission: true }],
//   undefined,
//   [{ user: true }],
// ]);
// console.log(result);
// { role: true, permission: true, user: true }
export const flattenNestedPermissions = (
  nestedPermissions: (Record<string, boolean>[] | undefined)[]
): Record<string, boolean> => {
  return nestedPermissions
    .filter((arr) => arr !== undefined) // 过滤 undefined
    .flat() // 展平嵌套数组
    .reduce(
      (acc, obj) => ({ ...acc, ...obj }),
      {} as Record<string, boolean>
    );
};
// 获取有效的服务类型
export const getAvailableServiceTypes = (
  projectId: string,
  SERVICE_TYPE_MAP: Record<string, Array<{ id: string; name: string }>>
): Array<{ id: string; name: string }> => {
  if (!projectId) return [];
  return SERVICE_TYPE_MAP[projectId] || [];
};